import { useEffect, useState } from "react";
import { Box, Button, Dialog, IconButton, MenuItem, Select, TextField, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { BTN_TEXT_SX, OUTLINED_BTN_SX, PRIMARY_BTN_SX, SECTION_SUBTITLE_SX, SECTION_TITLE_SX } from "./gate2Tokens";

/**
 * "Declare Build" — the exact build and environment a suite is executed
 * against. Gate 2 evidence is only meaningful when it names what was tested,
 * so a suite cannot enter the run until this is declared.
 */

export const ENVIRONMENTS = ["Development", "QA", "Staging", "Pre-production", "Production"];

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    height: 40,
    borderRadius: "4px",
    bgcolor: "#FFFFFF",
    fontFamily: "Lato, sans-serif",
    fontSize: 15,
    boxShadow: "inset 0px 2px 3px 1px rgba(85, 85, 85, 0.2)",
    "& fieldset": { borderColor: "#707070" },
    "&:hover fieldset": { borderColor: "#707070" },
    "&.Mui-focused fieldset": { borderColor: "#707070", borderWidth: "1px" },
  },
};

const labelSx = {
  fontFamily: "Lato, sans-serif",
  fontSize: 14,
  fontWeight: 700,
  lineHeight: "21px",
  color: "#707070",
  mb: 0.75,
};

export default function DeclareBuildDialog({ suite, build, onClose, onSave }) {
  const open = Boolean(suite);
  const [version, setVersion] = useState("");
  const [environment, setEnvironment] = useState("");
  const [commit, setCommit] = useState("");

  // Reopening for a different suite must show that suite's declaration, not
  // whatever the previous one left in the fields.
  useEffect(() => {
    if (!open) return;
    setVersion(build?.version || "");
    setEnvironment(build?.environment || "");
    setCommit(build?.commit || "");
  }, [open, suite?.id, build?.version, build?.environment, build?.commit]);

  const canSave = version.trim().length > 0 && Boolean(environment);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      PaperProps={{
        sx: {
          borderRadius: "8px",
          width: { xs: "calc(100% - 32px)", sm: 520 },
          maxWidth: 520,
          p: 4,
          position: "relative",
          boxShadow: "0px 10px 10px 0px rgba(0, 0, 0, 0.24)",
        },
      }}
      slotProps={{ backdrop: { sx: { bgcolor: "rgba(52, 64, 84, 0.45)" } } }}
    >
      <IconButton
        size="small"
        onClick={onClose}
        aria-label="Close"
        sx={{ position: "absolute", top: 8, right: 8, color: "#555555" }}
      >
        <CloseIcon sx={{ fontSize: 24 }} />
      </IconButton>

      <Typography sx={SECTION_TITLE_SX}>Declare Build</Typography>
      <Typography sx={SECTION_SUBTITLE_SX}>
        Record the exact build and environment “{suite?.name}” will be executed against. This is
        captured as evidence for the Gate 2 release decision.
      </Typography>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5, mt: 3 }}>
        <Box>
          <Typography sx={labelSx}>
            <Box component="span" sx={{ color: "#DE350B" }}>
              *
            </Box>
            Build / Version
          </Typography>
          <TextField
            fullWidth
            size="small"
            placeholder="e.g. credit-risk-api 2.1.4"
            value={version}
            onChange={(e) => setVersion(e.target.value)}
            sx={fieldSx}
          />
        </Box>

        <Box>
          <Typography sx={labelSx}>
            <Box component="span" sx={{ color: "#DE350B" }}>
              *
            </Box>
            Environment
          </Typography>
          <Select
            fullWidth
            displayEmpty
            value={environment}
            onChange={(e) => setEnvironment(e.target.value)}
            sx={{
              height: 40,
              borderRadius: "4px",
              bgcolor: "#FFFFFF",
              fontFamily: "Lato, sans-serif",
              fontSize: 15,
              boxShadow: "inset 0px 2px 3px 1px rgba(85, 85, 85, 0.2)",
              "& .MuiOutlinedInput-notchedOutline": { borderColor: "#707070" },
            }}
            renderValue={(v) =>
              v ? (
                v
              ) : (
                <Typography component="span" sx={{ fontFamily: "Lato, sans-serif", fontSize: 15, color: "#707070" }}>
                  Select environment
                </Typography>
              )
            }
          >
            {ENVIRONMENTS.map((env) => (
              <MenuItem key={env} value={env}>
                {env}
              </MenuItem>
            ))}
          </Select>
        </Box>

        <Box>
          <Typography sx={labelSx}>Commit / Artefact ref (optional)</Typography>
          <TextField
            fullWidth
            size="small"
            placeholder="e.g. 88b7bb0"
            value={commit}
            onChange={(e) => setCommit(e.target.value)}
            sx={fieldSx}
          />
        </Box>
      </Box>

      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.5, mt: 4 }}>
        <Button variant="outlined" disableElevation onClick={onClose} sx={OUTLINED_BTN_SX}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disableElevation
          disabled={!canSave}
          onClick={() =>
            onSave({ version: version.trim(), environment, commit: commit.trim() })
          }
          sx={{ ...PRIMARY_BTN_SX, ...BTN_TEXT_SX }}
        >
          Save Declaration
        </Button>
      </Box>
    </Dialog>
  );
}
